import * as api from '@/redux/api/saveItem';
import { createSelector } from '@reduxjs/toolkit';
import { fetchItemCardModelsIfNeeded, getItemCardModel } from './itemCardModel';
import { fetchSavedItemCountsIfNeeded } from './savedItemCount';
import { getAuthToken, getBidderId } from './user';
import { getDeployment } from './config';
import { getSaveItemAnalytics, getUnsaveItemAnalytics, trackConversionForAlgolia } from './analytics';
import { GlobalState } from '../rootReducer';
import {
    LOAD_BIDDER_DATA_SUCCESS,
    LOAD_SAVED_ITEMS_FAIL,
    LOAD_SAVED_ITEMS_REQUEST,
    LOAD_SAVED_ITEMS_SUCCESS,
    LOG_OUT_BIDDER,
    LOGIN_SUCCESS,
    SAVE_ITEM_FAIL,
    SAVE_ITEM_REQUEST,
    SAVE_ITEM_SUCCESS,
    UNSAVE_ITEM_FAIL,
    UNSAVE_ITEM_REQUEST,
    UNSAVE_ITEM_SUCCESS,
} from './actions';
import { PASSWORD_RESET_SUCCESS } from './passwordReset';
import cloneDeep from 'lodash/cloneDeep';

// reducer
export type SaveItemSlice = {
    ids: number[];
    loaded: number;
    loading: boolean;
    pagination: {
        keyword: string;
        page: number;
        pageSize: number;
        results: number;
        sort: string;
        totalRecords: number;
    };
    savedItems: {
        [id: number]: {
            itemId: number;
            saveTs: number;
            status: string;
        };
    };
};

export const defaultSaveItemSlice: SaveItemSlice = {
    ids: [], // current list of shown itemIds on the saved item page
    loaded: 0,
    loading: false,
    pagination: {
        keyword: '',
        page: 1,
        pageSize: 24,
        results: 0,
        sort: '-saved',
        totalRecords: 0,
    },
    savedItems: {},
};

export default function reducer(state: SaveItemSlice = defaultSaveItemSlice, action: any = {}): SaveItemSlice {
    let existing;

    switch (action.type) {
        case LOAD_SAVED_ITEMS_FAIL:
            return {
                ...state,
                loading: false,
            };
        case LOAD_SAVED_ITEMS_REQUEST:
            return {
                ...state,
                loading: true,
            };
        case LOAD_SAVED_ITEMS_SUCCESS:
            return {
                ...state,
                ids: action.payload.map((i) => i.itemId),
                loaded: action.meta.actionTime,
                loading: false,
                pagination: {
                    ...defaultSaveItemSlice.pagination,
                    ...action.meta.query,
                    totalRecords: action.meta.totalRecords || action.payload.length || 0,
                },
            };
        case LOAD_BIDDER_DATA_SUCCESS:
        case LOGIN_SUCCESS:
        case PASSWORD_RESET_SUCCESS:
            existing = {};

            if (action.payload.savedItemsTime) {
                action.payload.savedItemsTime.forEach((item) => {
                    existing[item.itemId] = { itemId: Number(item.itemId), saveTs: item.saveTs };
                    existing[item.itemId].status = 'SAVED';
                });
            }

            return {
                ...state,
                savedItems: existing,
            };

        case LOG_OUT_BIDDER:
            // reset saved Items to defaults
            return defaultSaveItemSlice;

        case SAVE_ITEM_FAIL:
            existing = cloneDeep(state.savedItems);
            delete existing[action.meta.itemId];
            return {
                ...state,
                savedItems: existing,
            };

        case UNSAVE_ITEM_REQUEST:
            existing = cloneDeep(state.savedItems);
            existing[action.payload].status = 'REMOVED';
            return {
                ...state,
                savedItems: existing,
            };

        case UNSAVE_ITEM_SUCCESS:
            return state;

        case SAVE_ITEM_REQUEST:
            existing = cloneDeep(state.savedItems);
            existing[action.payload.itemId] = {
                itemId: action.payload.itemId,
                saveTs: action.payload.saveTs,
                status: 'SAVED',
            };
            return {
                ...state,
                savedItems: existing,
            };

        case SAVE_ITEM_SUCCESS:
            return state;

        case UNSAVE_ITEM_FAIL:
            existing = cloneDeep(state.savedItems);
            existing[action.meta.itemId].status = 'SAVED';
            return {
                ...state,
                savedItems: existing,
            };

        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.saveItem;
const idSelector = (state: GlobalState, id: number) => id;

const savedItemsSelector = createSelector(stateSelector, (state) => state?.savedItems);

export const getSavedItem = createSelector([savedItemsSelector, idSelector], (savedItems, id) => savedItems[id]);
export const getSavedItemsLoading = createSelector(stateSelector, (state) => state.loading);
export const getSavedItems = createSelector(stateSelector, (state) => state.ids);
export const getSavedItemsPagination = createSelector(stateSelector, (state) => ({
    ...defaultSaveItemSlice.pagination,
    ...state.pagination,
}));

export const isItemSaved = createSelector(getSavedItem, (savedItem) => savedItem?.status === 'SAVED');

/* ACTION CREATORS */
const submitSaveItem = (itemId) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        dispatch({
            payload: { itemId, saveTs: Date.now() },
            type: SAVE_ITEM_REQUEST,
        });

        await api.postSaveItem({ authToken, deployment, itemId });
        const promises = [];
        if (!Boolean(getItemCardModel(state, itemId).itemId)) {
            promises.push(dispatch(fetchItemCardModelsIfNeeded([itemId], 'save-item')));
        }
        return Promise.all(promises).then(() => {
            const newState = getState();
            const analytics = getSaveItemAnalytics(newState, itemId);
            dispatch(trackConversionForAlgolia(itemId));
            dispatch({
                meta: analytics,
                payload: itemId,
                type: SAVE_ITEM_SUCCESS,
            });
            dispatch(fetchSavedItemCountsIfNeeded([itemId], true));
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { itemId },
            payload: error,
            type: SAVE_ITEM_FAIL,
        });
    }
};

const submitUnsaveItem = (itemId) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);

        dispatch({
            payload: itemId,
            type: UNSAVE_ITEM_REQUEST,
        });

        await api.postUnsaveItem({ authToken, deployment, itemId });
        const promises = [];
        if (!Boolean(getItemCardModel(state, itemId).itemId)) {
            promises.push(dispatch(fetchItemCardModelsIfNeeded([itemId], 'saved-item')));
        }
        return Promise.all(promises).then(() => {
            const newState = getState();
            const analytics = getUnsaveItemAnalytics(newState, itemId);
            dispatch({
                meta: analytics,
                payload: itemId,
                type: UNSAVE_ITEM_SUCCESS,
            });
            dispatch(fetchSavedItemCountsIfNeeded([itemId], true));
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { itemId },
            payload: error,
            type: UNSAVE_ITEM_FAIL,
        });
    }
};

export const fetchSavedItems = (query: api.SavedItemQueryType) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);

        dispatch({
            type: LOAD_SAVED_ITEMS_REQUEST,
        });

        const { payload } = await api.fetchSavedItems({ authToken, bidderId, deployment, query });

        dispatch({
            meta: {
                actionTime: Date.now(),
                pageCount: payload.pageCount,
                query,
                recordCount: payload.recordCount,
                totalRecords: payload.totalRecords,
            },
            payload: payload.items,
            type: LOAD_SAVED_ITEMS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: LOAD_SAVED_ITEMS_FAIL,
        });
    }
};

export const toggleItemSave = (itemId: number) => (dispatch: Function, getState: Function) => {
    if (isItemSaved(getState(), itemId)) {
        return dispatch(submitUnsaveItem(itemId));
    } else {
        return dispatch(submitSaveItem(itemId));
    }
};
